<template>
    <div class="competition-settings">
        <div v-if="!ready" class="row mat32">
            <div class="col-12">
                <div class="admin-header__loading-state">
                    Trwa wczytywanie
                    <div class="mal15 lds-ellipsis">
                        <div />
                        <div />
                        <div />
                    </div>
                </div>
            </div>
        </div>

        <template v-else>
            <div ref="messages">
                <panel-info-box v-if="successMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ successMessage }}</b>
                    </p>
                </panel-info-box>
                <panel-info-box v-if="errorMessage" class="mab16">
                    <p class="text-lg">
                        <b>{{ errorMessage }}</b>
                    </p>
                </panel-info-box>
            </div>

            <div class="lang-switcher mab16">
                <button
                    v-for="lang in langs"
                    :key="lang"
                    type="button"
                    class="lang-switcher__btn"
                    :class="{ 'lang-switcher__btn--active': activeLang === lang }"
                    @click="activeLang = lang"
                >
                    {{ lang.toUpperCase() }}
                </button>
            </div>

            <panel-competition-regulation-form
                :key="activeLang"
                v-model="forms[activeLang]"
                :saving="saving"
                :removing="removing"
                @save="saveSettings"
                @remove="removeStatute"
            />
        </template>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import PanelCompetitionRegulationForm from '@/components/PanelCompetitionRegulationForm';
    import PanelInfoBox from '@/components/PanelInfoBox';
    import { READ_PANEL_COMPETITION } from '@/store/actions.type';
    import {
        getStatute,
        uploadStatute,
        deleteStatute,
        updateStatuteLink,
        statuteLinkField,
        statuteFileField,
    } from '@/api/statute';

    const LANGS = ['pl', 'en', 'de', 'cz', 'fr', 'lt', 'ua'];

    function emptyLangForm() {
        return {
            type: 'file',
            file: [],
            link: '',
            current: {
                exists: false,
                path: null,
                type: null,
            },
        };
    }

    function emptyForms() {
        return LANGS.reduce((acc, lang) => {
            acc[lang] = emptyLangForm();
            return acc;
        }, {});
    }

    function isHttpUrl(value) {
        return /^https?:\/\//i.test(String(value || '').trim());
    }

    function isUploadedFilePath(value) {
        const text = String(value || '').trim();
        if (!text || isHttpUrl(text)) {
            return false;
        }
        if (/statute_files\//i.test(text) || /^\/?files\//i.test(text)) {
            return true;
        }
        return /\.pdf$/i.test(text) && text.indexOf('/') !== -1;
    }

    function normalizeExternalLink(value) {
        const text = String(value || '').trim();
        if (!text) {
            return '';
        }
        if (/^[a-z][a-z0-9+.-]*:/i.test(text)) {
            return text;
        }
        return `http://${text}`;
    }

    function toBase64Content(content) {
        if (!content || typeof content !== 'string') {
            return content;
        }
        const marker = 'base64,';
        const index = content.indexOf(marker);
        return index >= 0 ? content.slice(index + marker.length) : content;
    }

    function extractPath(response) {
        if (typeof response === 'string') {
            return response.replace(/^"+|"+$/g, '').trim();
        }
        if (!response || typeof response !== 'object') {
            return '';
        }
        if (typeof response.path === 'string') {
            return response.path.trim();
        }
        if (typeof response.url === 'string') {
            return response.url.trim();
        }
        return '';
    }

    function asNonEmpty(value) {
        if (value == null) {
            return '';
        }
        const text = String(value).trim();
        return text && text !== 'null' && text !== 'undefined' ? text : '';
    }

    function inferStatuteType(value, hintedType) {
        const text = asNonEmpty(value);
        if (isUploadedFilePath(text)) {
            return 'file';
        }
        if (text) {
            return 'link';
        }
        if (hintedType === 'file' || hintedType === 'link') {
            return hintedType;
        }
        return 'file';
    }

    function storedStatuteFromCompetition(competition, lang, override) {
        if (override !== undefined) {
            return asNonEmpty(override);
        }
        if (!competition) {
            return '';
        }
        return (
            asNonEmpty(competition[statuteLinkField(lang)]) ||
            asNonEmpty(competition[statuteFileField(lang)])
        );
    }

    function fileInfoExists(data) {
        if (data === true || data === 1 || data === '1') {
            return true;
        }
        if (!data || typeof data !== 'object') {
            return false;
        }
        return data.exists === true || data.exists === 1 || data.exists === '1';
    }

    function mapStatuteToForm(data, competition, lang, storedOverride) {
        const form = emptyLangForm();
        const stored = storedStatuteFromCompetition(competition, lang, storedOverride);
        const filePath = data === true ? stored : extractPath(data);
        const existsOnFile = fileInfoExists(data) || Boolean(filePath);
        const hintedType =
            data && typeof data === 'object' && (data.type === 'file' || data.type === 'link')
                ? data.type
                : null;
        const path = stored || filePath;
        const exists = Boolean(path) || existsOnFile;
        const type = exists ? inferStatuteType(path || stored, hintedType) : null;

        form.current = {
            exists,
            path: path || null,
            type,
        };
        if (exists && type === 'link') {
            form.type = 'link';
            form.link = path;
        } else {
            form.type = 'file';
        }
        return form;
    }

    export default {
        name: 'PanelCompetitionSettingRegulation',
        components: {
            PanelCompetitionRegulationForm,
            PanelInfoBox,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                saving: false,
                removing: false,
                langs: LANGS,
                activeLang: 'pl',
                forms: emptyForms(),
                successMessage: '',
                errorMessage: '',
            };
        },
        computed: {
            competitionId() {
                return this.competition.id;
            },
            activeForm() {
                return this.forms[this.activeLang] || emptyLangForm();
            },
        },
        async created() {
            await this.loadAll();
            this.ready = true;
        },
        methods: {
            ...mapActions({
                readPanelCompetition: READ_PANEL_COMPETITION,
            }),
            scrollToMessages() {
                this.$nextTick(() => {
                    const el = this.$refs.messages;
                    if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        return;
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
            },
            normalizeError(e) {
                if (!e) {
                    return 'Wystąpił błąd';
                }
                if (typeof e === 'string') {
                    return e;
                }
                if (e.error) {
                    return e.error;
                }
                if (e.message) {
                    return e.message;
                }
                return 'Wystąpił błąd';
            },
            formFromSources(lang, fileInfo, storedOverride) {
                return mapStatuteToForm(fileInfo, this.competition, lang, storedOverride);
            },
            async fetchFileInfo(lang) {
                try {
                    return await getStatute(this.competitionId, lang);
                } catch (e) {
                    return null;
                }
            },
            async loadLang(lang, storedOverride) {
                const data = await this.fetchFileInfo(lang);
                this.$set(this.forms, lang, this.formFromSources(lang, data, storedOverride));
            },
            async loadAll() {
                this.errorMessage = '';
                const results = await Promise.all(
                    LANGS.map(async (lang) => {
                        const data = await this.fetchFileInfo(lang);
                        return { lang, data };
                    })
                );
                const next = emptyForms();
                results.forEach((result) => {
                    next[result.lang] = this.formFromSources(result.lang, result.data);
                });
                this.forms = next;
            },
            async saveSettings() {
                const form = this.activeForm;
                const lang = this.activeLang;
                this.saving = true;
                this.successMessage = '';
                this.errorMessage = '';

                try {
                    let savedValue;
                    if (form.type === 'file') {
                        const uploaded = Array.isArray(form.file) ? form.file[0] : null;
                        if (uploaded && uploaded.content) {
                            const path = extractPath(
                                await uploadStatute(
                                    this.competitionId,
                                    {
                                        name: uploaded.name,
                                        content: toBase64Content(uploaded.content),
                                        mime: uploaded.mime || 'application/pdf',
                                    },
                                    lang
                                )
                            );
                            if (!path) {
                                throw new Error('Nie udało się wgrać pliku');
                            }
                            await updateStatuteLink(this.competitionId, lang, path);
                            savedValue = path;
                        } else if (form.current && form.current.exists && form.current.type === 'file') {
                            savedValue = form.current.path;
                        } else {
                            this.errorMessage = 'Wybierz plik PDF';
                            return;
                        }
                    } else {
                        const link = normalizeExternalLink(form.link);
                        if (!link) {
                            this.errorMessage = 'Podaj link do regulaminu';
                            return;
                        }
                        await updateStatuteLink(this.competitionId, lang, link);
                        savedValue = link;
                    }

                    await this.readPanelCompetition(this.competitionId);
                    await this.loadLang(lang, savedValue);
                    this.successMessage = 'Regulamin został zapisany';
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                } finally {
                    this.saving = false;
                    this.scrollToMessages();
                }
            },
            async removeStatute() {
                const lang = this.activeLang;
                this.removing = true;
                this.successMessage = '';
                this.errorMessage = '';
                try {
                    try {
                        await deleteStatute(this.competitionId, lang);
                    } catch (e) {
                        // File endpoint may 404 when only a link is stored; still clear Competition.
                    }
                    await updateStatuteLink(this.competitionId, lang, '');
                    await this.readPanelCompetition(this.competitionId);
                    await this.loadLang(lang, '');
                    this.successMessage = 'Regulamin został usunięty';
                } catch (e) {
                    this.errorMessage = this.normalizeError(e);
                } finally {
                    this.removing = false;
                    this.scrollToMessages();
                }
            },
        },
    };
</script>

<style scoped>
    .competition-settings {
        width: 100%;
    }
    .lang-switcher {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    .lang-switcher__btn {
        min-width: 48px;
        padding: 8px 14px;
        border: 1px solid var(--color-blue-grey, #78909c);
        border-radius: 4px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-weight: 600;
    }
    .lang-switcher__btn--active {
        background: var(--color-dark-pastel-green, #66bb6a);
        border-color: var(--color-dark-pastel-green, #66bb6a);
        color: #fff;
    }
    @media (min-width: 992px) {
        .competition-settings {
            margin-left: 30px;
        }
    }
</style>
